import React from 'react';
import cn from 'classnames';

import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import Icon from 'components/generic/Icon.component';
import IconStatus from 'components/generic/IconStatus.component';
import InlineSpinner from 'components/generic/InlineSpinner.component';
import Space from 'components/generic/Space.component';
import CurrencySign from 'components/price/CurrencySign.component';
import USER_ROLE from 'constants/role';
import PURCHASE_VARIANTS, { UK_BUNDLES, US_BUNDLES } from 'constants/sms/purchase.const';
import { withLocale, withTranslation } from 'providers/GILT.provider';

import PickerSmsSelections from 'clientCommunications/components/Clients/common/PickerSmsSelections';

import CN from './PurchaseSmsCreditDialog.module.scss';

const SMS_CREDITS_LIMIT = 20;

const determineBundles = (locale, t) => (locale === 'en-GB' ? UK_BUNDLES(t) : US_BUNDLES(t));

class PurchaseSmsCreditDialog extends React.Component {
  static defaultProps = {
    fromRequestDeposit: false,
    handleOpen: null,
    showPurchasePopup: null,
    fetchSalon: () => {},
    gotoBilling: () => {},
    variant: 'default',
  };

  constructor(props) {
    super(props);
    const isNotReceptionist = props.permission !== USER_ROLE.receptionist;
    this.state = {
      selectedSmsCostWithoutVat: '',
      selectedSmsValue: '',
      selectedSmsCost: '',
      selectedSmsCostIncVat: '',
      selectedOption: isNotReceptionist ? 'two_thousands' : 'fifty',
      selections: determineBundles(props.locale, props.t),
      displayLoading: false,
      displayPurchase: true,
      displaySuccessOrFailure: false,
      confirmDisabled: true,
    };
  }

  // noinspection JSCheckFunctionSignatures
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.isFetching !== this.props.isFetching) {
      this.setState({ displayLoading: nextProps.isFetching });
    }
    if (this.props.isFetching && !nextProps.isFetching) {
      this.setState({
        displaySuccessOrFailure: true,
        displayLoading: false,
        displayPurchase: false,
      });
    }
  }

  onCancel = () => {
    this.props.closePopup('purchaseModal');
  };
  onConfirm = () => {
    if (this.state.confirmDisabled) {
      return;
    }
    this.props.purchaseSmsBundle({
      bundle_count: this.state.selectedOption,
      salon: this.props.salonId.toString(),
    });
    this.setState({ displayLoading: true, displayPurchase: false, confirmDisabled: true });
  };

  switchRadio = (index) => {
    const { selections } = this.state;

    const newSelections = selections.map((selection) => {
      selection.checked = false;
      return selection;
    });
    newSelections[index].checked = true;

    const selected = newSelections[index];
    this.setState({
      selections: newSelections,
      selectedOption: selected.name,
      selectedSmsCost: selected.smsCost,
      selectedSmsValue: selected.smsValue,
      selectedSmsCostWithoutVat: selected.smsCostWithoutVat,
      selectedSmsCostIncVat: selected.smsCostIncVat,
    });
  };
  enableConfirm = (tf) => {
    this.setState({ confirmDisabled: !tf });
  };

  render() {
    const { sms_credits_available: smsCredits, variant, variantButton, t } = this.props;
    const { selectedSmsValue, selectedSmsCost, selectedSmsCostIncVat } = this.state;

    const isNoCredits = !smsCredits;

    const renderModalTitle = () => {
      if (isNoCredits) return t('composite.purchaseSmsCreditDialog.noCredits');
      if (variant === PURCHASE_VARIANTS.lowCredit) return t('composite.purchaseSmsCreditDialog.remainingCredits');
      return t('composite.purchaseSmsCreditDialog.purchaseCredits');
    };

    const warningClasses = cn({
      [CN.textWarning]: true,
      [CN.textError]: isNoCredits,
      [CN.textSuccess]: smsCredits > SMS_CREDITS_LIMIT,
    });

    const pickerButtonsClasses = cn({
      [CN.pickerButtonsContainer]: true,
      [CN.withSpace]: variant !== PURCHASE_VARIANTS.lowCredit,
    });

    return (
      <div>
        <SalonModal tag="ModalsPanel__modal_progress" isOpen={this.state.displayLoading} variant="noFooter">
          <SalonModal.Body>
            <div className={CN.modalContent}>
              <div className={CN.modalTitle}>{t('composite.purchaseSmsCreditDialog.title')}</div>

              <div className={CN.modalText}>
                <div className={CN.modalSpinner}>
                  <InlineSpinner variant="small" />
                </div>
                {t('composite.purchaseSmsCreditDialog.loadingText')}
              </div>
            </div>
          </SalonModal.Body>
        </SalonModal>

        <SalonModal tag="PurchaseSmsCreditDialog__purchase" isOpen={this.state.displayPurchase} withFooter>
          <SalonModal.Header>
            <div className={CN.closeButtonContainer}>
              <Button variant="empty" onClick={this.onCancel}>
                <Icon variant="close" variantInvert />
              </Button>
            </div>
          </SalonModal.Header>
          <SalonModal.Body>
            {isNoCredits && (
              <IconStatus variant="warning" width="lg">
                <Icon variant="warningCircle" width="l1" />
              </IconStatus>
            )}

            <div
              className={cn({
                [CN.modalContent]: true,
                [CN.lowCreditVariant]: variant === PURCHASE_VARIANTS.lowCredit,
              })}
            >
              <div className={CN.modalTitle}>{renderModalTitle()}</div>
              {variant === PURCHASE_VARIANTS.lowCredit && <div className={warningClasses}>{smsCredits || 0}</div>}
              <div className={CN.modalText}>
                <div>
                  {variant !== PURCHASE_VARIANTS.lowCredit ? (
                    <div>{t('composite.purchaseSmsCreditDialog.selectBundleTitle')}</div>
                  ) : (
                    <div className={CN.lowCreditSubText}> {t('composite.purchaseSmsCreditDialog.topUp')}</div>
                  )}

                  <div className={pickerButtonsClasses}>
                    <PickerSmsSelections
                      selections={this.state.selections}
                      onClick={this.switchRadio}
                      permission={this.props.permission}
                      enableConfirm={this.enableConfirm}
                      variant={variantButton}
                    />
                  </div>
                </div>
              </div>
            </div>
          </SalonModal.Body>
          <SalonModal.Footer>
            <div className={CN.modalButtons}>
              {variant === PURCHASE_VARIANTS.lowCredit ? (
                <div className={CN.footerText}>{t('composite.purchaseSmsCreditDialog.lowCreditMessage')}</div>
              ) : (
                <div className={CN.footerText}>
                  {t('composite.purchaseSmsCreditDialog.defaultCreditMessagePart1')} <CurrencySign />
                  50 {t('composite.purchaseSmsCreditDialog.defaultCreditMessagePart2')} <CurrencySign />1
                </div>
              )}

              <div className={CN.footerButtons}>
                <Button variant="primaryForce" onClick={this.onConfirm} xSpace disabled={this.state.confirmDisabled}>
                  <span className={CN.buttonText}>
                    {variant === PURCHASE_VARIANTS.lowCredit
                      ? t('composite.purchaseSmsCreditDialog.buyCredits')
                      : t('common.buttons.confirmUpp')}
                  </span>
                </Button>
              </div>
            </div>
          </SalonModal.Footer>
        </SalonModal>

        <SalonModal
          tag="PurchaseSmsCreditDialog__confirm"
          isOpen={!this.state.displayLoading && this.state.displaySuccessOrFailure && !this.props.errorMessage}
          variant="noFooter"
        >
          <SalonModal.Header>
            <div className={CN.closeButtonContainer}>
              <Button
                variant="empty"
                onClick={() => {
                  this.setState({ displaySuccessOrFailure: false });
                  if (this.props.handleOpen) {
                    this.props.handleOpen(false, 'after purchase');
                  } else if (this.props.showPurchasePopup) {
                    this.props.showPurchasePopup(false);
                  }
                  if (this.props.fetchSalon) {
                    this.props.fetchSalon(this.props.salonId);
                  }
                }}
              >
                <Icon variant="close" variantInvert />
              </Button>
            </div>
          </SalonModal.Header>
          <SalonModal.Body>
            <IconStatus width="lg" variant="success">
              <Icon selected width="l1" variant="confirm" />
            </IconStatus>
            <div className={CN.modalContent}>
              <div className={CN.modalTitle}>{t('composite.purchaseSmsCreditDialog.confirmModal.title')}</div>
              <div className={CN.modalText}>
                {t('composite.purchaseSmsCreditDialog.confirmModal.descriptionPart1', { credits: selectedSmsValue })}
                <br />
                <Space>
                  <CurrencySign value={selectedSmsCost} />
                </Space>
                {t('composite.purchaseSmsCreditDialog.confirmModal.descriptionPart2')}
                <CurrencySign value={selectedSmsCostIncVat} />
                {t('composite.purchaseSmsCreditDialog.confirmModal.descriptionPart3')}
              </div>
              <br />

              <div className={CN.modalText}>{t('composite.purchaseSmsCreditDialog.confirmModal.totalCredits')}</div>
              <div className={CN.textAlert}>{smsCredits || selectedSmsValue}</div>
            </div>
          </SalonModal.Body>
        </SalonModal>

        {!this.state.displayLoading && this.state.displaySuccessOrFailure && this.props.errorMessage && (
          <div className="popup-2-button animated zoomIn">
            <div className="modal-header-marketing" style={{ borderRadius: '4px' }}>
              <div className="flex-center">
                <div className="modal-content">
                  <div className="sms-title">
                    <div>
                      {t('composite.purchaseSmsCreditDialog.paymentFailed.title')}
                      <div style={{ fontSize: '20px', marginTop: '20px', fontFamily: 'ProximaNova-r' }}>
                        {t('composite.purchaseSmsCreditDialog.paymentFailed.description')}
                      </div>
                    </div>
                  </div>
                  <p className="uniq-note text-smaller">{t('composite.purchaseSmsCreditDialog.paymentFailed.note')}</p>
                </div>
              </div>
            </div>
            <div className="footer-marketing cfix">
              <div className="info-text">
                <p />
              </div>
              <div className="marketing-button-container">
                <button
                  className="sms-btn-white"
                  style={{ marginRight: '46px' }}
                  onClick={() => {
                    this.setState({ displaySuccessOrFailure: false });
                    if (this.props.handleOpen) {
                      this.props.handleOpen(false, 'after purchase');
                    }
                    this.props.closePopup('purchaseModal');
                  }}
                >
                  {t('common.buttons.goBackUpp')}
                </button>
                <button
                  className="sms-btn-violet"
                  onClick={() => {
                    this.setState({ displaySuccessOrFailure: false });
                    this.props.closePopup('smsComposeModalOpen');
                    this.props.gotoBilling();
                  }}
                >
                  {t('composite.purchaseSmsCreditDialog.paymentFailed.billingButton')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

const PurchaseSmsCreditDialogHoc = withLocale(withTranslation()(PurchaseSmsCreditDialog));

export default PurchaseSmsCreditDialogHoc;
